import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { CornerBadge, Position } from '@sorare/core/src/components/CornerBadge';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { cart } from '@sorare/core/src/lib/glossary';

import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';

import type { PriceLockedRibbon_primaryOffer } from './__generated__/index.graphql.ts';

const Anchor = styled.div`
  position: absolute;
  top: var(--ribbon-top, var(--unit));
  left: var(--ribbon-left, auto);
  right: var(--ribbon-right, auto);
  transform: var(--ribbon-transform, none);
  z-index: 2;
`;

type Props = {
  primaryOffer: PriceLockedRibbon_primaryOffer;
  position?: 'top-left' | 'top-right';
  // Center on the parent's top border (over blocks/rows) vs. inset inside it (over card art).
  straddle?: boolean;
};

const SIDE_STYLES: Record<
  NonNullable<Props['position']>,
  { align: Position; style: Record<string, string> }
> = {
  'top-left': { align: Position.LEFT, style: { '--ribbon-left': '0' } },
  'top-right': { align: Position.RIGHT, style: { '--ribbon-right': '0' } },
};

const STRADDLE_STYLE = {
  '--ribbon-top': '0',
  '--ribbon-transform': 'translateY(-50%)',
};

export const PriceLockedRibbon = ({
  primaryOffer,
  position = 'top-right',
  straddle = false,
}: Props) => {
  const { isCampaign, soldOut, endDate, color } =
    getSteppedPrimaryOffer(primaryOffer);

  if (!isCampaign) return null;

  const { align, style } = SIDE_STYLES[position];

  return (
    <Anchor
      style={{ ...style, ...(straddle ? STRADDLE_STYLE : {}) } as CSSProperties}
    >
      {/* Same supply band color as the stock counter's gauge and text. */}
      <CornerBadge align={align} color={color}>
        {soldOut ? (
          <BrandXS color="var(--c-black)">
            <FormattedMessage {...cart.soldOut} />
          </BrandXS>
        ) : (
          <>
            <FontAwesomeIcon icon={iconLock} color="var(--c-black)" size="xs" />
            <BrandXS color="var(--c-black)">
              {!!endDate && <TimeLeft time={endDate} />}
            </BrandXS>
          </>
        )}
      </CornerBadge>
    </Anchor>
  );
};

PriceLockedRibbon.fragments = {
  primaryOffer: gql`
    fragment PriceLockedRibbon_primaryOffer on TokenPrimaryOffer {
      id
      ...SteppedPrimaryOffer_primaryOffer
    }
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
  ` as TypedDocumentNode<PriceLockedRibbon_primaryOffer>,
};
